import {
  createHttpClient,
  IHttpClient,
  IHttpRequestConfig,
} from '@merchstores/frontend/utils/http';
import { resolveMerchStoreApiUrl } from '../merchstore';

export const SHOPIFY_MERCH_SERVICES_STOREFRONT_URL =
  process.env.REACT_APP_SHOPIFY_MERCH_SERVICES_STOREFRONT_URL;
export const SHOPIFY_MERCH_SERVICES_STOREFRONT_TOKEN =
  process.env.REACT_APP_SHOPIFY_MERCH_SERVICES_STOREFRONT_TOKEN;
export const SHOPIFY_STOREFRONT_PROXY =
  process.env.REACT_APP_SHOPIFY_STOREFRONT_PROXY;

export const DEFAULT_API_VERSION = '2023-07';

export function resolveApiUrl(version?: string): string {
  const apiVersion = version || DEFAULT_API_VERSION;

  const apiUrl = (SHOPIFY_MERCH_SERVICES_STOREFRONT_URL || '').replace(
    /\/api\/\d{4}-\d{2}/g,
    `/api/${apiVersion}`
  );

  return apiUrl;
}

interface ICreateStorefrontApiClientOptions {
  endpointUrl?: string;
  apiKey?: string;
  useProxy?: boolean;
}

export function createStorefrontApiClient(
  options?: ICreateStorefrontApiClientOptions
): IHttpClient {
  options = options || {};

  let endpointUrl = options.endpointUrl || resolveApiUrl();
  const apiKey = options.apiKey || SHOPIFY_MERCH_SERVICES_STOREFRONT_TOKEN;
  const useProxy = options.useProxy;

  if (useProxy || SHOPIFY_STOREFRONT_PROXY) {
    endpointUrl = resolveMerchStoreApiUrl(
      '/.netlify/functions/merchstore-api/shopifyStorefront'
    );
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (apiKey) {
    headers['X-Shopify-Storefront-Access-Token'] = apiKey;
  }

  const httpClient = createHttpClient({ baseURL: endpointUrl, headers });

  // A GraphQL answer with errors becomes a StorefrontApiError; otherwise data is the GraphQL data.
  return {
    post: async (path, data, options) => {
      const response = await httpClient.post(path, data, options);

      if (response && response.data && response.data.errors) {
        throw new StorefrontApiError(
          'Storefront API Error: ' + JSON.stringify(response.data.errors),
          {
            errors: response.data.errors,
            request: response.config,
          }
        );
      }

      response.data = response.data.data;

      return response;
    },
  };
}

export type IStorefrontErrorList = Array<{ message: string }>;
export interface IStorefrontGraphQLQuery {
  query: string;
  variables?: unknown;
}

export interface IErrorInfo {
  errors: IStorefrontErrorList;
  query: IStorefrontGraphQLQuery;
  request: IHttpRequestConfig;
}

export class StorefrontApiError extends Error {
  protected request;
  protected errors;

  constructor(
    msg: string,
    data: { request: IHttpRequestConfig; errors: IStorefrontErrorList }
  ) {
    super(msg);
    data = data || {};
    this.request = data.request;
    this.errors = data.errors;
  }

  getErrors(): IStorefrontErrorList {
    return this.errors;
  }

  getRequest(): IHttpRequestConfig {
    return this.request;
  }

  getQuery(): IStorefrontGraphQLQuery {
    return JSON.parse(this.request.data || '{}');
  }

  getInfo(): IErrorInfo {
    return {
      errors: this.getErrors(),
      query: this.getQuery(),
      request: this.getRequest(),
    };
  }
}
